import { t } from '@lingui/macro';
import { FieldValues, UseFormSetError } from 'react-hook-form';

type ContactValidationError = Pick<
  GlobalContactError,
  'field' | 'code' | 'message'
>;

/**
 * Maps server-side contact validation errors (the `errors` array returned by the
 * create/update global contact mutations) onto the contact form fields.
 *
 * The mutation now returns validation failures in its payload (HTTP 200) rather
 * than as a top-level GraphQL error, so callers read `data.*.errors` and pass it
 * here. Recognized errors are surfaced on their field. Returns true only when
 * every error was recognized, so the caller shows a generic fallback
 * notification whenever any error was left unhandled.
 */
export default function applyContactValidationErrors(
  errors: ContactValidationError[],
  setError: UseFormSetError<FieldValues>,
): boolean {
  let allRecognized = true;

  const surface = (field: string, message: string) => {
    setError(field, { type: 'manual', message });
  };

  errors.forEach(({ field, code, message }) => {
    if (field === 'email' && code === 'taken') {
      surface('email', t`This email is already being used by another contact.`);
    } else if (field === 'email' && code === 'invalid') {
      surface('email', t`Email must be a valid email address`);
    } else if (field === 'mobile_phone' && code === 'taken') {
      surface(
        'mobilePhone',
        t`This mobile phone number is already being used by another contact.`,
      );
    } else if (
      (field === 'mobile_phone' && code === 'invalid') ||
      field === 'mobile_phone_country_code' ||
      (field === 'base' && message.includes('country code'))
    ) {
      surface(
        'mobilePhoneCountryCode',
        t`This country code is not valid for the mobile phone number.`,
      );
    } else {
      // Unrecognized error: leave it for the caller's generic fallback.
      allRecognized = false;
    }
  });

  return allRecognized;
}
